export type UserFacingAction =
  | "sync"
  | "create"
  | "open"
  | "import"
  | "save"
  | "sign-in"
  | "generate"
  | "billing"
  | "generic";

const FALLBACKS: Record<UserFacingAction, string> = {
  sync: "Couldn't save to your account just now. Your work is still on this device.",
  create: "The book is on this device, but it didn't reach your account yet. We'll try again shortly.",
  open: "Couldn't open that book. Check your connection and try again.",
  import: "Imported on this device, but it didn't reach your account yet.",
  save: "Couldn't save just now. Your latest edits are still in this window.",
  "sign-in": "Couldn't sign in. Check your connection and try again.",
  generate: "Couldn't finish that just now. Try again in a moment.",
  billing: "Couldn't open billing. Try again in a moment.",
  generic: "Something went wrong. Try again in a moment.",
};

const UNREACHABLE: Record<UserFacingAction, string> = {
  sync: "Couldn't reach your account just now. Your work is still on this device.",
  create: "The book is on this device, but it didn't reach your account yet. We'll try again shortly.",
  open: "Couldn't open that book. Check your connection and try again.",
  import: "Imported on this device, but it didn't reach your account yet.",
  save: "Couldn't save just now. Your latest edits are still in this window.",
  "sign-in": "Couldn't sign in. Check your connection and try again.",
  generate: "Couldn't reach the assistant. Try again in a moment.",
  billing: "Couldn't open billing. Try again in a moment.",
  generic: "Couldn't reach the server. Try again in a moment.",
};

const OFFLINE_SYNC =
  "You're offline or the connection dropped. Your work is on this device and will sync when you're back online.";
const OFFLINE_GENERATE = "You're offline. Reconnect and try the assistant again.";

/** Browser/WebKit messages when fetch never got a response — not the same as being offline. */
function isBrowserNetworkError(raw: string): boolean {
  if (/^(failed to fetch|load failed|networkerror when attempting to fetch resource\.?)$/i.test(raw)) {
    return true;
  }
  if (/networkerror/i.test(raw)) return true;
  if (/ECONNREFUSED|ENOTFOUND|ETIMEDOUT|ECONNRESET/i.test(raw)) return true;
  if (/\bCORS\b/i.test(raw)) return true;
  if (/the internet connection appears to be offline/i.test(raw)) return true;
  return false;
}

function clientLooksOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error == null) return "";
  return String(error);
}

function looksTechnical(raw: string): boolean {
  return (
    /prisma/i.test(raw) ||
    /invocation/i.test(raw) ||
    /ECONNREFUSED|ENOTFOUND|ETIMEDOUT|ECONNRESET/i.test(raw) ||
    /\b(TypeError|ReferenceError|SyntaxError|PrismaClient)\b/i.test(raw) ||
    /\bat\s+\S+\s+\(/.test(raw) ||
    /`[a-z_]+\.[a-z_]+/i.test(raw) ||
    /failed \(\d{3}\)/i.test(raw) ||
    /\bstatus\s*[:=]?\s*\d{3}\b/i.test(raw) ||
    /https?:\/\//i.test(raw) ||
    raw.includes("\n") ||
    raw.length > 140
  );
}

function looksHuman(raw: string): boolean {
  if (!raw || raw.length > 140) return false;
  if (/[`{}<>]|prisma|\.tsx?\b|\.jsx?\b/i.test(raw)) return false;
  return !looksTechnical(raw);
}

/** Map API / runtime errors to a short sentence a writer can act on. */
export function userFacingError(error: unknown, action: UserFacingAction = "generic"): string {
  const raw = errorText(error).trim();
  const fallback = FALLBACKS[action];
  if (!raw) return fallback;

  if (/unauthorized|not signed in|please sign in|\b401\b/i.test(raw)) {
    if (action === "sign-in") return FALLBACKS["sign-in"];
    if (action === "generate") return "Please sign in again to use the assistant.";
    return "Please sign in again to save to your account.";
  }
  if (/forbidden|BOOK_FORBIDDEN|don't have access|\b403\b/i.test(raw)) {
    return "You don't have access to that book.";
  }
  if (/\bnot found\b|\b404\b/i.test(raw)) {
    return action === "open" ? "That book isn't in your account." : fallback;
  }
  if (/rate limit|too many requests|\b429\b/i.test(raw)) {
    return "That's a bit busy right now. Try again in a moment.";
  }
  if (/internal server error|bad gateway|service unavailable|\b50[234]\b/i.test(raw)) {
    return fallback;
  }
  if (isBrowserNetworkError(raw) || /the internet connection appears to be offline/i.test(raw)) {
    if (clientLooksOffline() || /the internet connection appears to be offline/i.test(raw)) {
      return action === "generate" ? OFFLINE_GENERATE : OFFLINE_SYNC;
    }
    return UNREACHABLE[action];
  }
  if (looksTechnical(raw)) return fallback;
  if (looksHuman(raw)) return raw;
  return fallback;
}
